import { observable, action, computed, makeAutoObservable } from 'mobx';
import { CategoryHeaderPropsType } from 'src/components/CmsCasinoManagement/CategoryHeader/CategoryHeader';
import {
    CategorySettingsOptionType,
    CategorySettingsValueType,
} from 'src/components/CmsCasinoManagement/CategoryHeader/SettingsDropdown/SettingsDropdown';
import { FormInputState, FormModel } from 'src_common/common/mobx-utils/Form';
import { DEFAULT_CATEGORY_NAME } from 'src/domains/casino/models/models';
import { getUniverseModule } from 'src/domains/common/universes';
import { Common } from 'src/AppState/Common';

export class CategoryHeaderState {
    @observable public categoryInputState: FormInputState<string>;
    @observable public categoryInputModel: FormModel<string>;
    @observable public isEditingCategoryName: boolean = false;
    @observable private currentCategoriesOrder: Array<number> = [];

    public constructor(
        private readonly common: Common,
        private readonly props: CategoryHeaderPropsType
    ) {
        this.categoryInputState = new FormInputState(props.category.name, false);
        this.categoryInputModel = this.categoryInputState.toModel();
        makeAutoObservable(this);
    }

    @action public onEditCategoryName = (): void => {
        if (this.isFixedCategory) {
            return;
        }
        this.isEditingCategoryName = true;
    };

    @action public onSaveNewCategoryName = async (): Promise<void> => {
        const { editCategory } = this.props.liveGamesState.collectionData;
        const newName = this.categoryInputState.value.trim();

        const categoryNames = this.props.liveGamesState.categories
            .map((category) => category.name)
            .filter((name) => name !== this.props.category.name);

        const nameAlreadyExists = categoryNames.includes(newName);

        if (nameAlreadyExists || newName.length === 0 || newName.length > 24) {
            return;
        }

        if (newName !== this.props.category.name) {
            await editCategory(this.props.category.id, {
                name: newName,
                active: this.props.category.active,
                visible_in_navigation: this.props.category.visibleInNavigation,
                visible_in_all_games: this.props.category.visibleInAllGames,
                game_ids: this.props.category.gameIds,
            });
        }
        this.isEditingCategoryName = false;
    };

    @action public onRemoveCategory = async (): Promise<void> => {
        const { onRemoveCategory } = this.props.liveGamesState.collectionData;
        await onRemoveCategory(this.props.category.id);
    };

    @action public onCopyUrlToClipboard = async (): Promise<void> => {
        const { urls } = getUniverseModule(this.common);
        const collectionSlug = this.props.liveGamesState.collection;

        const url = `${urls.website}${collectionSlug}?tabId=${this.props.category.id}`;

        await navigator.clipboard.writeText(url);
    };

    @action public onCategoryOrderUp = async (categoryId: number): Promise<void> => {
        if (this.isEditingCategoryName || this.isNewCategory || this.isGamesListEmpty) {
            return;
        }

        const { getCategoryPosition, reorderCategories, categoryIds } = this.props.liveGamesState.collectionData;
        const currentPosition = getCategoryPosition(categoryId);
        if (currentPosition === 0) {
            return;
        }
        const newPosition = currentPosition - 1;
        this.currentCategoriesOrder = categoryIds.filter((id: number) => id !== categoryId);
        this.currentCategoriesOrder.splice(newPosition, 0, categoryId);

        await reorderCategories(this.currentCategoriesOrder, { categoryId, newPosition });
    };

    @action public onCategoryOrderDown = async (categoryId: number): Promise<void> => {
        if (this.isEditingCategoryName || this.isNewCategory || this.isGamesListEmpty) {
            return;
        }

        const { getCategoryPosition, reorderCategories, categoryIds } = this.props.liveGamesState.collectionData;
        const newPosition = getCategoryPosition(categoryId) + 1;
        this.currentCategoriesOrder = categoryIds.filter((id: number) => id !== categoryId);
        this.currentCategoriesOrder.splice(newPosition, 0, categoryId);

        await reorderCategories(this.currentCategoriesOrder, { categoryId, newPosition });
    };

    @computed public get isCategoryActive(): boolean {
        return this.props.category.active;
    }

    @computed public get isGamesListEmpty(): boolean {
        return this.props.category.gameIds.length === 0;
    }

    public get isNewCategory(): boolean {
        return this.props.category.name === DEFAULT_CATEGORY_NAME;
    }

    public get isFixedCategory(): boolean {
        return this.props.category.fixed;
    }

    @action public onSettingsChange = async (selectedOptions: Array<CategorySettingsOptionType>): Promise<void> => {
        const { editCategory } = this.props.liveGamesState.collectionData;

        let active = false;
        let visible_in_navigation = false;
        let visible_in_all_games = false;

        for (const selectedOption of selectedOptions) {
            if (selectedOption.value === 'active') {
                active = true;
            }

            if (selectedOption.value === 'visible_in_navigation') {
                visible_in_navigation = true;
            }

            if (selectedOption.value === 'visible_in_all_games') {
                visible_in_all_games = true;
            }
        }

        await editCategory(this.props.category.id, {
            name: this.props.category.name,
            active: active,
            visible_in_navigation: visible_in_navigation,
            visible_in_all_games: visible_in_all_games,
            game_ids: this.props.category.gameIds,
        });
    };

    @computed public get categorySettingsList(): Array<CategorySettingsOptionType> {
        return [
            {
                value: 'active',
                label: 'Active',
                disabled: this.isEditingCategoryName || this.isGamesListEmpty,
            },
            {
                value: 'visible_in_navigation',
                label: 'Visible in navigation',
                disabled: this.isCategoryActive === false || this.isEditingCategoryName || this.isGamesListEmpty,
            },
            {
                value: 'visible_in_all_games',
                label: 'Visible in all games',
                disabled: this.isCategoryActive === false || this.isEditingCategoryName || this.isGamesListEmpty,
            },
        ];
    }

    @computed public get categorySettingsSelected(): Array<CategorySettingsValueType> {
        const selectedSettings: Array<CategorySettingsValueType> = [];

        if (this.props.category.active) {
            selectedSettings.push('active');

            if (this.props.category.visibleInNavigation) {
                selectedSettings.push('visible_in_navigation');
            }

            if (this.props.category.visibleInAllGames) {
                selectedSettings.push('visible_in_all_games');
            }
        }

        return selectedSettings;
    }
}
